import { AbilityBuilder, createMongoAbility } from '@casl/ability';
import type { MongoAbility } from '@casl/ability';
import { campusCarCondition } from './campus.js';
import { catalogueDefaultPermissionsForRoleNames } from './rolePermissionCatalogue.js';
import type { Subject } from './subjects.js';

export type Action =
  | 'create'
  | 'read'
  | 'update'
  | 'delete'
  | 'manage' // CASL wildcard for all actions
  | 'cancel'
  | 'confirm'
  | 'checkout'
  | 'checkin'
  | 'complete'
  | 'extend'
  | 'refund'
  | 'block'
  | 'unblock'
  | 'restore'
  | 'ban'
  | 'verify'
  | 'upload'
  | 'update-status'
  /**
   * 189 (#34, decision D3): moving a booking's pickup/return window. Its own action rather than a
   * reuse of `update-status` or a `manage` check at the route, because who may do it is a role
   * decision — the same reason `checkout`, `checkin` and `complete` are separate from
   * `update-status` rather than folded into it.
   */
  | 'reschedule'
  /**
   * 189 (#34, decision D1/D3): setting a booking's effective total. Granted to nobody beyond the
   * administrators who already hold `manage 'Booking'`, and named anyway: a provider may reschedule
   * but must not re-price, and widening this later should be a grant here rather than an edit to
   * `PATCH /bookings/:id/price-override`.
   */
  | 'override-price'
  /** 372 (#1240): approve or reject a new or increased gift certificate or voucher (not the creator's own). */
  | 'approve'
  /** 341 (#934): accept a payment flagged as the wrong amount (322) without `manage Booking`. */
  | 'accept-mismatch'
  /** 341 (#934): see every review in the admin; not `read`, which everyone holds for public reviews. */
  | 'moderate'
  /**
   * 288: reassign a money-settled booking to a different car after a breakdown, price frozen. Its
   * own action rather than a reuse of `reschedule` — the replacement car is deliberately NOT
   * restricted to the requesting supplier's own fleet (spec FR-006), the opposite of the reasoning
   * that kept the ordinary `changeCar` action on `manage` alone (research.md D2).
   */
  | 'reassignCarOnBreakdown'
  /**
   * 303 (spec FR-002): assigning/clearing a car's Host owner. Its own action, granted only to
   * Supplier (+ Admin via `manage`), rather than folded into the existing `update Car` grant — a
   * Host already holds `update Car` (narrowed to their own assigned car), and FR-002 requires them
   * to be refused this specifically, INCLUDING on their own car. A shared `update` grant could
   * never express that distinction; a separate action can.
   */
  | 'assign-host';

export type AppAbility = MongoAbility<[Action, Subject]>;

export interface AbilityUser {
  _id: string;
  role_ids: string[];
  roles?: Array<{ name: string }>;
  supplier_id?: string | null;
  /**
   * 207 (#313, research.md R1): the caller's resolved permission set, already flattened across
   * every role they hold whose grants are permission-driven (today: Admin, Supplier, Host — never
   * SuperAdmin or Customer, which stay hardcoded below and never read this field). Resolved by the
   * CALLER (`backend/src/shared/ability.ts`'s `buildAbilityFromUser`, `admin/src/context/
   * AuthContext.tsx`'s `buildAbilityUser`) before this function runs, so `defineAbility` itself
   * stays pure and synchronous — no DB access, no divergence risk between the two consumers
   * (Constitution IV).
   */
  permissions?: Array<{
    subject: Subject;
    action: string;
    scope: 'any' | 'own_supplier' | 'own_assigned_cars';
  }>;
  /**
   * 303: car ids the caller (a Host) is currently assigned to — resolved per request by
   * `backend/src/app.ts`'s auth preHandler (`CarModel.find({host_membership_id: ...})`), the same
   * shape `currentUserMemberships` already uses. Omitted or `undefined` reads as `[]` when building
   * an `own_assigned_cars` condition (never as "skip the condition entirely," which would grant
   * everything). See specs/303-host-car-assignment/research.md R1/R2.
   */
  assignedCarIds?: string[];
  /**
   * 330: the customer's campus (a pickup location id), or absent when not chosen yet. Read only by the
   * Customer block below: a customer reads the cars at their campus, and no car before choosing one.
   */
  campus_location_id?: string | null;
}

/**
 * 303: the condition a resolved permission's `scope` builds, now subject-aware rather than merely
 * scope-aware — `own_assigned_cars` matches a DIFFERENT field depending on whether the subject IS
 * `Car` (`_id`) or is something that HANGS OFF a car (`car_id`: `Booking`, `CarBlock`,
 * `MaintenanceRecord`, `Review`, `BookingInspection`). `own_supplier`'s condition is unchanged and
 * still subject-agnostic — every subject it applies to already carries its own `supplier_id`.
 */
function conditionForScope(
  scope: 'any' | 'own_supplier' | 'own_assigned_cars',
  subject: Subject,
  user: AbilityUser,
): Record<string, unknown> | undefined {
  if (scope === 'own_supplier') return { supplier_id: user.supplier_id ?? null };
  if (scope === 'own_assigned_cars') {
    const ids = user.assignedCarIds ?? [];
    return subject === 'Car' ? { _id: { $in: ids } } : { car_id: { $in: ids } };
  }
  return undefined;
}

/**
 * Builds CASL ability rules for a given user based on their roles.
 *
 * SuperAdmin and Customer are hardcoded, unconditional exceptions (207, #313) — SuperAdmin because
 * an unconditional "manage everything" grant is never worth storing as rows, Customer because an
 * accidental edit to it would be the single highest-blast-radius mistake available (every
 * customer's own-booking access). Every OTHER role name (Admin, Supplier, Host today, and any role
 * added later) is resolved entirely from `user.permissions` — this function does not special-case
 * those three literal names.
 */
export function defineAbility(user: AbilityUser | null): AppAbility {
  const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility);

  if (!user) {
    // Guest — read-only public access
    can('read', 'Car');
    can('read', 'Location');
    can('read', 'Review');
    can('read', 'Feature');
    // 043: public browse-filter taxonomy (not CarModel)
    can('read', 'CarBrand');
    can('read', 'CarCategory');
    can('read', 'CarFeature');
    return build();
  }

  const roleNames = user.roles?.map((r) => r.name) ?? [];

  // 207 (#313, research.md R1): resolved, permission-driven grants — applies to whatever role(s)
  // the caller already resolved into `user.permissions`, never SuperAdmin or Customer (neither is
  // ever assigned permissions by the migration, and both return early below regardless).
  //
  // `user.permissions === undefined` (omitted entirely, distinct from a deliberately resolved
  // empty `[]`) falls back to the catalogue's own default for whichever of Admin/Supplier/Host
  // appear in `roleNames` — the honest reading of "this caller's permissions were never resolved
  // at all" (a caller predating this feature, most callers in either `backend/`'s or `admin/`'s own
  // test suites, or a real Role document `.lean()`-read before the migration ever touched it) as
  // opposed to "resolved, and genuinely holds nothing." A caller that HAS resolved permissions —
  // even down to `[]` after a deliberate edit — must pass that array explicitly; only true
  // omission triggers this.
  const resolvedPermissions =
    user.permissions ?? catalogueDefaultPermissionsForRoleNames(roleNames);
  for (const permission of resolvedPermissions) {
    const condition = conditionForScope(permission.scope, permission.subject, user);
    // CASL's `can()` overloads resolve per-subject condition shapes from a LITERAL subject; a
    // dynamically-typed subject/action pair (necessarily so here — it's data, not code) cannot be
    // resolved that way, matching this file's existing precedent for the same limitation
    // (`can('read', 'User', {_id: user._id} as never)`, further down in the Customer block). `never`, not `any`: it
    // satisfies the overload without switching type checking off.
    can(permission.action as Action, permission.subject, condition as never);
  }

  const isSuperAdmin = roleNames.includes('SuperAdmin');
  const isCustomer = roleNames.includes('Customer');

  if (isSuperAdmin) {
    // Super admin can do everything
    can('manage', 'all');
    return build();
  }

  // 207 (#313): Admin, Supplier, and Host no longer have hardcoded branches here — the loop above
  // resolves them entirely from `user.permissions`, seeded from exactly what these branches used
  // to grant (`packages/casl-abilities/src/rolePermissionCatalogue.ts`, migration
  // `207-seed-role-permissions.ts`). Deleted only after the full existing test suite proved
  // identical behavior with the loop alone (research.md R6) — see git history for the removed
  // branches if ever needed for reference.

  if (isCustomer) {
    // Customer — own data only
    const ownCondition = { user_id: user._id };
    // 330 (FR-015): the cars at the customer's campus, from the one shared definition; none before
    // a campus is chosen. Guests and staff are not bound (their blocks above and below).
    const campusCars = campusCarCondition(user.campus_location_id);
    if (campusCars) can('read', 'Car', campusCars);
    can('read', 'Location');
    can('read', 'Feature');
    can('read', 'Review');
    // 043: public browse-filter taxonomy (not CarModel)
    can('read', 'CarBrand');
    can('read', 'CarCategory');
    can('read', 'CarFeature');
    can('create', 'Booking', ownCondition);
    can('read', 'Booking', ownCondition);
    can('cancel', 'Booking', ownCondition);
    can('read', 'Payment', ownCondition);
    can('create', 'Review', ownCondition);
    can('update', 'Review', ownCondition); // 042: author-only edit
    can('read', 'Notification', ownCondition);
    can('update', 'Notification', ownCondition);
    can('create', 'PushSubscription', ownCondition);
    can('delete', 'PushSubscription', ownCondition);
    // 210 (#306): unconditioned — BookingInspection carries no user_id for a condition to match.
    // Ownership is enforced in route code against the referenced Booking, the same shape
    // Supplier/Host already use for this exact subject.
    can('create', 'BookingInspection');
    can('read', 'User', { _id: user._id } as never);
    can('update', 'User', { _id: user._id } as never);
    can('upload', 'FileAsset');
    // 376 (#1262): no `cannot('read', 'User')` here. A customer is given no other `read User` rule, so they already see
    // only their own account; the `cannot` (added after the permission rules) took that access away from a staff member
    // who is also a customer, e.g. an Admin could no longer open other users.
    return build();
  }

  // Default fallback — authenticated but no role at all. 207 (#313): guarded on `roleNames.length
  // === 0` specifically — a user HOLDING a role (Admin/Supplier/Host today) must be governed
  // entirely by the loop above and get nothing more, even if it resolved zero permissions (e.g. a
  // resolver bug, or a role newly created with nothing assigned yet). Falling through into this
  // block for such a user would silently grant unconditioned public reads on top of — or instead
  // of — whatever the loop scoped, exactly the regression the existing test suite caught here
  // during this feature's own equivalence proof (research.md R6): Supplier's scoped `read Car`
  // was joined by an unconditioned `read Car` from this block, making every OTHER supplier's car
  // readable too.
  if (roleNames.length === 0) {
    can('read', 'Car');
    can('read', 'Location');
    can('read', 'Feature');
    can('read', 'Review');
  }

  return build();
}
